import { Fragment, h, JSX } from "@stencil/core";
import { Account, TLink, TMenu } from "@phn/entities/content";
import { shopStateStore } from "@phn/state/shop-state-store";
import { isShopAvailable } from "@phn/utility/helper/shop-helper";
import { navContentStore } from "@phn/state/nav-content-store";
import { getAccountRoutingKey } from "@phn/entities/routing-key";
import { EventActions } from "@phn/utility/constants";
import { finderStateStore } from "@phn/state/finder-state-store";
import { navStateStore } from "@phn/state/nav-state-store";
import { getUsername, IsLoggedIn } from "@phn/utility/helper";
import { sendDataLayerEvent } from "../../services/analytics/data-layer-event-sender";

export default function Links({
  content,
  services,
  onLogoutClick,
  showSkeleton,
}: {
  content: Account;
  services: TMenu | null;
  showSkeleton: boolean;
  onLogoutClick: () => void;
}): JSX.Element {
  const { loggedInState, unreadMessagesCount, app, locale } =
    navStateStore.state;
  const isLoggedIn = IsLoggedIn(loggedInState) || false;

  const {
    links,
    messages,
    savedSearches,
    savedVehicles,
    onlineOrderHistory,
    connect,
    welcome,
    settings,
  } = content;
  const { savedSearchesCount, savedVehiclesCount } = finderStateStore.state;
  const { wishlistItemsNumber } = shopStateStore.state.shopInformation;
  const { shop } = navContentStore.state;
  const fixedLinkElements: JSX.Element[] = [];
  const myPorscheAppId = "mainmenu.account.myporscheapp";
  const onlinebillPaymentId = "mainmenu.services.onlinebillpayment";
  const contactId = "mainmenu.account.contact";

  // Need to re-arrange overview from the variable links array to the first position, should actually be a fixed item in content
  const myPorscheApp: TLink = links.find((link) => link.id === myPorscheAppId);
  const onlinebillPayment: TLink = links.find(
    (link) => link.id === onlinebillPaymentId,
  );
  const contact: TLink = links.find((link) => link.id === contactId);

  if (isShopAvailable(shop, locale, app) && shop?.wishlist) {
    fixedLinkElements.push(
      MenuItemCountLink({
        content: shop.wishlist,
        count: wishlistItemsNumber,
        showSkeleton,
      }),
    );
  }

  if (onlinebillPayment) {
    fixedLinkElements.push(
      MenuItemLink({ content: onlinebillPayment, showSkeleton }),
    );
  }

  if (myPorscheApp) {
    fixedLinkElements.push(
      MenuItemLink({ content: myPorscheApp, showSkeleton }),
    );
  }

  if (isLoggedIn && messages) {
    fixedLinkElements.push(
      MenuItemCountLink({
        content: messages,
        count: unreadMessagesCount,
        showSkeleton,
      }),
    );
  }

  if (savedSearches && savedSearches.text !== "") {
    fixedLinkElements.push(
      MenuItemCountLink({
        content: savedSearches,
        count: savedSearchesCount,
        showSkeleton,
      }),
    );
  }

  if (savedVehicles && savedVehicles.text !== "") {
    fixedLinkElements.push(
      MenuItemCountLink({
        content: savedVehicles,
        count: savedVehiclesCount,
        showSkeleton,
      }),
    );
  }

  if (connect) {
    fixedLinkElements.push(MenuItemLink({ content: connect, showSkeleton }));
  }

  if (isLoggedIn && onlineOrderHistory && onlineOrderHistory.text !== "") {
    fixedLinkElements.push(
      MenuItemLink({ content: onlineOrderHistory, showSkeleton }),
    );
  }

  if (contact) {
    fixedLinkElements.push(MenuItemLink({ content: contact, showSkeleton }));
  }

  // Everything below onlineOrderHistory is variable on the content side and can be re-arranged there
  const variableLinkElements: JSX.Element[] = links
    .filter((link: TLink) => link.id !== myPorscheAppId && link.id !== onlinebillPaymentId)
    .map<JSX.Element>((link: TLink) => {
      if (link.id === contactId) {
        return null;
      }

      return MenuItemLink({ content: link, showSkeleton });
    });

  const username = getUsername(
    loggedInState,
    navContentStore.state.account?.loggedInFallback ?? "",
  );

  return (
    <Fragment>
      <div class="account-links">
        {isLoggedIn ? (
          <phn-p-text size="inherit" class="headline fs-mask">
            {welcome}, {username}
          </phn-p-text>
        ) : null}
        {showSkeleton ? (
          <div class="skeleton-menu">
            {[...fixedLinkElements, ...variableLinkElements].map(() => (
              <div class="skeleton-menu-item" />
            ))}
          </div>
        ) : null}
        <div
          style={{ display: showSkeleton ? "none" : "", width: "100%" }}
          class="account-links-wrapper"
        >
          <div
            class={`${isLoggedIn ? "logged-in" : "logged-out"} account-links-container`}
          >
            {services ? (
              <div class="account-link-item sc-phn-nd-account-drawer">
                <phn-p-text
                  size="small"
                  color="contrast-medium"
                  class="subline"
                >
                  {services.text}
                </phn-p-text>
              </div>
            ) : null}

            {fixedLinkElements}

            {isLoggedIn ? (
              <div class="account-link-item sc-phn-nd-account-drawer">
                <phn-p-text
                  size="small"
                  color="contrast-medium"
                  class="subline"
                >
                  {settings}
                </phn-p-text>

                {variableLinkElements}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {isLoggedIn ? (
        <div class="account-link-item sc-phn-nd-account-drawer log-out-wrapper">
          {LogoutLink({ content, onLogoutClick, showSkeleton })}
        </div>
      ) : null}
    </Fragment>
  );
}

function MenuItemCountLink({
  content,
  count,
  showSkeleton,
}: {
  content: TLink | null;
  count: number;
  showSkeleton: boolean;
}): JSX.Element | null {
  return content ? (
    <div class="account-link-item" key={content.id}>
      <phn-nd-menu-item
        elementId={getAccountRoutingKey(content.id)}
        class={`${showSkeleton ? "skeleton" : ""}`}
        analyticsId={content.id}
        text={content.text}
        link={content.link}
        datalayerEventAction={EventActions.NAVIGATION_LINK_CLICK}
      >
        <MenuItemCountIndicator count={count} />
      </phn-nd-menu-item>
    </div>
  ) : null;
}

function LogoutLink({
  content,
  onLogoutClick,
  showSkeleton,
}: {
  content: Account;
  showSkeleton: boolean;
  onLogoutClick: () => void;
}): JSX.Element {
  const { logout } = content;

  return (
    <div class="account-link-item" key={logout.id}>
      <phn-p-button
        variant="secondary"
        class={`account-menu-item logout ${showSkeleton ? "skeleton" : ""}`}
        onClick={() => {
          sendDataLayerEvent({
            analyticsId: logout.id,
            text: logout.text,
            elementId: getAccountRoutingKey(logout.id),
            clickElementType: "link",
            link: "",
            customDatalayerProperties: {},
            datalayerEventAction: EventActions.NAVIGATION_LINK_CLICK,
          });
          onLogoutClick();
        }}
      >
        {logout.text}
      </phn-p-button>
    </div>
  );
}

function MenuItemLink({
  content,
  showSkeleton,
}: {
  content: TLink | null;
  showSkeleton: boolean;
}): JSX.Element | null {
  return content ? (
    <div class="account-link-item" key={content.id}>
      <phn-nd-menu-item
        elementId={getAccountRoutingKey(content.id)}
        class={`${showSkeleton ? "skeleton" : ""}`}
        analyticsId={content.id}
        text={content.text}
        link={content.link}
        datalayerEventAction={EventActions.NAVIGATION_LINK_CLICK}
      />
    </div>
  ) : null;
}

function MenuItemCountIndicator({
  count,
}: {
  count: number;
}): JSX.Element | null {
  if (count <= 0) return null;

  return (
    <phn-p-tag
      slot="suffix"
      class="menu-item-count"
      color="notification-info-soft"
    >
      {count}
    </phn-p-tag>
  );
}
